import React from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";

import { patientMatchingRequest } from "api/patientMatchingRequest";
import { loginRequest } from "features/authConfig";
import { ciamSessionRequest } from "api/ciamSessionRequest";
import { ciamSessionSwitchRequest } from "api/ciamSessionSwitchRequest";
import { PatientSelector } from "./PatientSelector";
import { FullScreenLoader } from "components/FullScreenLoader/FullScreenLoader";

const PatientSelectorContainer: React.FC<{
  patientList?: any;
  idToken?: string;
}> = (props) => {
  const { instance, accounts, inProgress } = useMsal();

  const [patientList, setPatientList] = React.useState<any[]>(
    props.patientList ?? []
  );
  const [idToken, setIdToken] = React.useState<string>(props.idToken ?? "");

  // Only for the initial patient matching fetch
  const [initialLoading, setInitialLoading] = React.useState<boolean>(
    !(props.patientList && props.idToken)
  );
  const [initialSlow, setInitialSlow] = React.useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);
  const retryTimerRef = React.useRef<number | null>(null);
  const retriesRef = React.useRef(0);
  const MAX_RETRIES = 3;
  const RETRY_DELAY_MS = 10000;

  // Run-once guard so we don't call matching multiple times
  const executedRef = React.useRef(false);

  React.useEffect(() => {
    // If props already gave us everything, don't refetch
    if (props.patientList && props.idToken) {
      setPatientList(props.patientList ?? []);
      setIdToken(props.idToken ?? "");
      setInitialLoading(false);
      return;
    }

    // Wait until MSAL finishes redirect/account hydration
    if (inProgress !== InteractionStatus.None) return;

    // Don't re-run after we already kicked off matching
    if (executedRef.current) return;

    const activeAccount = instance.getActiveAccount() || accounts?.[0];
    if (!activeAccount) return;

    executedRef.current = true;
    setInitialLoading(true);

    let cancelled = false;

    const runMatching = async () => {
      let retryScheduled = false;
      let backgroundStarted = false;
      try {
        const tokenResp = await instance.acquireTokenSilent({
          account: activeAccount,
          scopes: loginRequest.scopes,
        });

        const token = tokenResp?.idToken || "";
        setIdToken(token);

        if (!token) {
          setPatientList([]);
          return;
        }

        let offset = 0;
        let allMatches: any[] = [];

        setIsLoadingMore(false);

        const firstBatch = await patientMatchingRequest({
          ciam_token: token,
          offset,
        });

        const firstMatched = firstBatch?.matched_patient ?? [];
        allMatches = allMatches.concat(firstMatched);
        setPatientList(allMatches);

        const more = !!firstBatch?.overFifty;
        offset =
          typeof firstBatch?.nextOffset === "number"
            ? firstBatch.nextOffset
            : offset + 50;

        // Release the UI after the first 50
        setInitialLoading(false);

        if (more) {
          backgroundStarted = true;
          setIsLoadingMore(true);
          (async () => {
            try {
              let overFifty = true;
              while (overFifty) {
                const nextBatch = await patientMatchingRequest({
                  ciam_token: token,
                  offset,
                });

                const matched = nextBatch?.matched_patient ?? [];
                allMatches = allMatches.concat(matched);
                setPatientList(allMatches);

                overFifty = !!nextBatch?.overFifty;
                offset =
                  typeof nextBatch?.nextOffset === "number"
                    ? nextBatch.nextOffset
                    : offset + 50;
              }
            } finally {
              setIsLoadingMore(false);
            }
          })();
        }

        // No automatic session switch for single-patient case; handled by user selection in PatientSelector
      } catch (e: any) {
        const status = e?.response?.status;
        if (status === 503 && retriesRef.current < MAX_RETRIES) {
          retriesRef.current += 1;
          if (retryTimerRef.current) window.clearTimeout(retryTimerRef.current);
          retryTimerRef.current = window.setTimeout(() => {
            if (!cancelled) runMatching();
          }, RETRY_DELAY_MS);
          retryScheduled = true;
          return;
        }
        setPatientList([]);
      } finally {
        if (!cancelled) {
          if (retryTimerRef.current) {
            window.clearTimeout(retryTimerRef.current);
            retryTimerRef.current = null;
          }
          if (!retryScheduled && initialLoading) {
            setInitialLoading(false);
          }
          if (!backgroundStarted) {
            setIsLoadingMore(false);
          }
        }
      }
    };

    runMatching();

    return () => {
      cancelled = true;
      if (retryTimerRef.current) {
        window.clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, [props.patientList, props.idToken, inProgress, accounts, instance]);

  React.useEffect(() => {
    if (!initialLoading) {
      setInitialSlow(false);
      return;
    }
    const timer = window.setTimeout(() => {
      setInitialSlow(true);
    }, 30000);
    return () => {
      window.clearTimeout(timer);
    };
  }, [initialLoading]);

  // ✅ ONLY ONE LOADER: show it while initial data is still loading
  if (initialLoading) {
    return <FullScreenLoader />;
  }

  // ✅ No overlay loader, ever
  return (
    <PatientSelector
      patientList={patientList}
      idToken={idToken}
      openLoader={() => {}}
      closeLoader={() => {}}
      isLoadingMore={isLoadingMore}
    />
  );
};

export default PatientSelectorContainer;
